import React, { useState, useRef, useEffect } from 'react';
import { Sidebar, SidebarBody, SidebarLink, useSidebar } from './ui/sidebar';
import { Home, BookOpen, Layers, FileText, Headphones, HelpCircle, Sun, Moon, LogOut, User, Target, Crown, Plus, MessageSquare, Trash2, Brain, ChevronLeft, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../lib/utils';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useAuth } from '../AuthContext';
import { User as UserType } from '../types';
import { isFeatureEnabled } from '../config/featureFlags';
import { useEntitlements } from '../hooks/useEntitlements';
import { getOspEntitlementStatus, ChatSummary, OspEntitlementStatus } from '../services/ospFirebaseService';
import { VERBAL_TOPICS, ANALYTICAL_TOPICS, getDailyChallengePrompt, QUICK_DRILL_PROMPT } from '../constants/ospActivities';

interface SidebarNewProps {
    user: UserType;
    activeSection: string;
    onNavigate: (section: string) => void;
    dashboardMode?: 'ZSV' | 'OSP';
    setDashboardMode?: (mode: 'ZSV' | 'OSP') => void;
    ospChatList?: ChatSummary[];
    activeChatId?: string | null;
    onSelectChat?: (chatId: string) => void;
    onNewChat?: () => void;
    onDeleteChat?: (chatId: string) => void;
    onAutoPrompt?: (prompt: string) => void;
}

export const SidebarNew: React.FC<SidebarNewProps> = ({ user, activeSection, onNavigate, dashboardMode = 'ZSV', setDashboardMode, ospChatList, activeChatId, onSelectChat, onNewChat, onDeleteChat, onAutoPrompt }) => {
    const [open, setOpen] = useState(false);
    const { isDarkMode, toggleDarkMode } = useDarkMode();
    const { logout } = useAuth();
    const { hasPro, hasPlus, userDoc } = useEntitlements();

    // OSP Entitlement
    const ospEntitlement = getOspEntitlementStatus(userDoc as any);

    // Determine ring color and styles based on tier
    const getRingStyles = () => {
        if (hasPro) {
            return 'ring-2 ring-yellow-400 shadow-[0_0_0_3px_rgba(250,204,21,0.25)]';
        }
        if (hasPlus) {
            return 'ring-2 ring-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.25)]';
        }
        return 'ring-2 ring-light-text-secondary/20 dark:ring-dark-text-secondary/20';
    };

    // Helper to close sidebar on mobile when navigating
    const closeSidebarOnMobile = () => {
        if (window.matchMedia("(max-width: 768px)").matches) {
            setOpen(false);
        }
    };

    const links = [
        { label: 'Domů', id: 'home', icon: <Home className="h-6 w-6 md:h-7 md:w-7 flex-shrink-0" />, lottieSrc: '/materials/icons/home.json' },
        { label: 'Můj plán', id: 'plan', icon: <Target className="h-6 w-6 md:h-7 md:w-7 flex-shrink-0" />, lottieSrc: '/materials/icons/target_animation.lottie', iconScale: 1.8 },
        { label: 'Moje Kurzy', id: 'courses', icon: <BookOpen className="h-6 w-6 md:h-7 md:w-7 flex-shrink-0" />, lottieSrc: '/materials/icons/chapter.json' },
        { label: 'Flashkarty', id: 'flashcards', icon: <Layers className="h-6 w-6 md:h-7 md:w-7 flex-shrink-0" />, lottieSrc: isDarkMode ? '/materials/icons/flashcarddark.json' : '/materials/icons/flashcardlight.json' },
        { label: 'Testy', id: 'testy', icon: <FileText className="h-6 w-6 md:h-7 md:w-7 flex-shrink-0" />, lottieSrc: '/materials/icons/testy.json' },
        { label: 'Audiokniha', id: 'audiobook', icon: <Headphones className="h-6 w-6 md:h-7 md:w-7 flex-shrink-0" />, lottieSrc: '/materials/icons/audio book.lottie' },
    ];

    return (
        <Sidebar open={open} setOpen={setOpen} user={user} getRingStyles={getRingStyles} hasPro={hasPro} hasPlus={hasPlus} onNavigate={onNavigate} logout={logout} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} ospEntitlement={ospEntitlement} dashboardMode={dashboardMode}>
            <SidebarBody className="justify-between gap-6">
                <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                    {/* Logo - always pinned, never scrolls */}
                    <Logo />

                    {/* Mode Switcher - Only confirm if feature is enabled */}
                    {isFeatureEnabled('OSP_TUTOR_MODULE', user.email) && (
                        <div className="mt-4 mb-2 px-2 flex-shrink-0">
                            {open ? (
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onNavigate('home');
                                        }}
                                        className={cn(
                                            "flex items-center justify-center h-12 text-xs font-bold rounded-xl border-2 border-b-[4px] transition-all duration-200 active:border-b-2 active:translate-y-[2px]",
                                            dashboardMode === 'ZSV'
                                                ? "border-brand-orange bg-[#fed7aa] text-brand-orange translate-y-[2px] border-b-2"
                                                : "border-gray-200 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                                        )}
                                    >
                                        ZSV
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onNavigate('osp');
                                            if (dashboardMode === 'ZSV') {
                                                closeSidebarOnMobile();
                                            }
                                        }}
                                        className={cn(
                                            "flex items-center justify-center h-12 text-xs font-bold rounded-xl border-2 border-b-[4px] transition-all duration-200 active:border-b-2 active:translate-y-[2px]",
                                            dashboardMode === 'OSP'
                                                ? "border-brand-orange bg-[#fed7aa] text-brand-orange translate-y-[2px] border-b-2"
                                                : "border-gray-200 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                                        )}
                                    >
                                        OSP
                                    </button>
                                </div>
                            ) : (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="w-full flex justify-center"
                                >
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const newMode = dashboardMode === 'ZSV' ? 'OSP' : 'ZSV';
                                            if (newMode === 'OSP') onNavigate('osp');
                                            else onNavigate('home');
                                        }}
                                        className={cn(
                                            "flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-200 border-2 border-b-[4px] active:border-b-2 active:translate-y-[2px] aspect-square",
                                            "bg-white dark:bg-neutral-800 border-gray-200 dark:border-neutral-700",
                                            "hover:bg-neutral-50 dark:hover:bg-neutral-700",
                                            "text-brand-orange font-bold text-xs"
                                        )}
                                        title={dashboardMode === 'ZSV' ? "Přepnout na OSP" : "Přepnout na ZSV"}
                                    >
                                        {dashboardMode === 'ZSV' ? "ZSV" : "OSP"}
                                    </button>
                                </motion.div>
                            )}
                        </div>
                    )}

                    {/* Scrollable nav / chat list — only this part scrolls */}
                    <div className="flex flex-col gap-2 flex-1 overflow-y-auto overflow-x-hidden no-scrollbar mt-5">
                        {dashboardMode === 'ZSV' && links.map((link) => {
                            if (link.id === 'testy' && !isFeatureEnabled('TESTY_MODULE')) return null;

                            // Map profile to settings for navigation if needed, or keep as profile
                            const navId = link.id === 'profile' ? 'settings' : link.id;
                            const isActive = activeSection === navId || (link.id === 'profile' && activeSection === 'settings');

                            return (
                                <SidebarLink
                                    key={link.id}
                                    link={{
                                        label: link.label,
                                        href: '#',
                                        icon: link.icon,
                                        lottieSrc: link.lottieSrc,
                                        iconScale: (link as any).iconScale,
                                        isActive: isActive,
                                        onClick: () => {
                                            onNavigate(navId);
                                            closeSidebarOnMobile();
                                        }
                                    }}
                                    className={cn(
                                        isActive
                                            ? "bg-primary/10 text-primary font-medium"
                                            : "text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700",
                                        "rounded-md transition-colors"
                                    )}
                                />
                            );
                        })}

                        {/* PRO Upgrade Button - inline with nav links, after Audiokniha */}
                        {dashboardMode === 'ZSV' && !ospEntitlement?.isPro && (
                            <SidebarLink
                                link={{
                                    label: ospEntitlement?.status === OspEntitlementStatus.TRIAL_NOT_ELIGIBLE
                                        ? 'ZÍSKAT PREMIUM'
                                        : 'PREMIUM ZDARMA',
                                    href: '#',
                                    icon: <Crown className="h-6 w-6 flex-shrink-0" />,
                                    onClick: () => onNavigate('upgrade')
                                }}
                                className={cn(
                                    "justify-center relative",
                                    "bg-yellow-400 hover:bg-yellow-300",
                                    "text-yellow-950 font-black tracking-wide",
                                    "rounded-xl transition-all duration-200",
                                    "border-b-[4px] border-yellow-600",
                                    "active:border-b-0 active:translate-y-[4px]",
                                    "px-3 py-3 mt-2",
                                    "shadow-none",
                                    !open ? "px-0" : "",
                                    "hidden md:flex"
                                )}
                            />
                        )}

                        {dashboardMode === 'OSP' && (
                            <>
                                {open && (
                                    <div className="mb-6 mt-2">
                                        <OspActivitiesMenu onAutoPrompt={(prompt) => {
                                            onAutoPrompt?.(prompt);
                                            closeSidebarOnMobile();
                                        }} />
                                    </div>
                                )}
                                {/* New Chat Button */}
                                <div
                                    onClick={() => { onNewChat?.(); closeSidebarOnMobile(); }}
                                    className={cn(
                                        "flex items-center gap-3 py-3 px-3 cursor-pointer rounded-lg transition-colors",
                                        "text-primary hover:bg-primary/10 font-medium"
                                    )}
                                >
                                    <Plus className="h-6 w-6 md:h-7 md:w-7 flex-shrink-0" />
                                    <motion.span
                                        animate={{
                                            display: open ? "inline-block" : "none",
                                            opacity: open ? 1 : 0,
                                        }}
                                        className="text-sm font-semibold whitespace-nowrap"
                                    >
                                        Nový chat
                                    </motion.span>
                                </div>

                                {/* Divider */}
                                {open && ospChatList && ospChatList.length > 0 && (
                                    <div className="border-t border-border my-1 mx-2" />
                                )}

                                {/* Chat History List */}
                                {ospChatList?.map((chat) => (
                                    <div
                                        key={chat.chatId}
                                        onClick={() => { onSelectChat?.(chat.chatId); closeSidebarOnMobile(); }}
                                        className={cn(
                                            "flex items-center gap-3 py-2 px-3 cursor-pointer rounded-lg transition-colors group",
                                            chat.chatId === activeChatId
                                                ? "bg-primary/10 text-primary font-medium"
                                                : "text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                                        )}
                                    >
                                        <MessageSquare className="h-5 w-5 flex-shrink-0 opacity-60" />
                                        <motion.span
                                            animate={{
                                                display: open ? "inline-block" : "none",
                                                opacity: open ? 1 : 0,
                                            }}
                                            className="text-sm whitespace-nowrap overflow-hidden text-ellipsis flex-1 min-w-0"
                                        >
                                            {chat.title}
                                        </motion.span>
                                        {open && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDeleteChat?.(chat.chatId);
                                                }}
                                                className="flex-shrink-0 p-1 rounded text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    {/* User Profile with dropdown menu - hidden on mobile, shown on desktop */}
                    <ProfileMenu
                        user={user}
                        getRingStyles={getRingStyles}
                        hasPro={hasPro}
                        hasPlus={hasPlus}
                        isDarkMode={isDarkMode}
                        toggleDarkMode={toggleDarkMode}
                        activeSection={activeSection}
                        onNavigate={(section: string) => {
                            onNavigate(section);
                            closeSidebarOnMobile();
                        }}
                        logout={() => {
                            logout();
                            closeSidebarOnMobile();
                        }}
                    />
                </div>
            </SidebarBody>
        </Sidebar>
    );
};

const ProfileMenu: React.FC<{
    user: UserType;
    getRingStyles: () => string;
    hasPro: boolean;
    hasPlus: boolean;
    isDarkMode: boolean;
    toggleDarkMode: () => void;
    activeSection: string;
    onNavigate: (section: string) => void;
    logout: () => void;
}> = ({ user, getRingStyles, hasPro, hasPlus, isDarkMode, toggleDarkMode, activeSection, onNavigate, logout }) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { open, animate } = useSidebar();
    const [showMenu, setShowMenu] = useState(false);
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleMouseEnter = () => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
        setShowMenu(true);
    };

    const handleMouseLeave = () => {
        hideTimeoutRef.current = setTimeout(() => {
            setShowMenu(false);
        }, 200);
    };

    useEffect(() => {
        return () => {
            if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        };
    }, []);

    return (
        <div
            className={cn("relative hidden md:block")}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {/* Profile trigger */}
            <div
                onClick={() => onNavigate('settings')}
                className={cn(
                    "flex items-center gap-3 group/sidebar py-2 px-2 cursor-pointer rounded-md transition-colors",
                    "text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                )}
            >
                <div className="flex-shrink-0 relative h-9 w-9">
                    {user.avatar ? (
                        <img
                            src={user.avatar}
                            className={cn("h-9 w-9 rounded-full object-cover", getRingStyles())}
                            alt="Avatar"
                        />
                    ) : (
                        <div className={cn(
                            "h-9 w-9 rounded-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center",
                            getRingStyles()
                        )}>
                            <User className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                        </div>
                    )}
                    {hasPro && (
                        <span className="absolute -top-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full bg-gray-900 text-[8px] shadow-sm ring-1 ring-white">
                            👑
                        </span>
                    )}
                    {!hasPro && hasPlus && (
                        <span className="absolute -top-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full bg-blue-600 text-[8px] text-white font-bold shadow-sm ring-1 ring-white">
                            +
                        </span>
                    )}
                </div>
                <motion.span
                    animate={{
                        display: animate ? (open ? "inline-block" : "none") : "inline-block",
                        opacity: animate ? (open ? 1 : 0) : 1,
                    }}
                    className="text-base font-semibold whitespace-pre inline-block !p-0 !m-0"
                >
                    {user.name}
                </motion.span>
            </div>

            {/* Dropdown popover */}
            <AnimatePresence>
                {showMenu && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 10 }}
                        transition={{ duration: 0.15 }}
                        className={cn(
                            "absolute bottom-full mb-2 w-52 bg-card dark:bg-card rounded-lg shadow-lg ring-1 ring-black/10 dark:ring-white/10 z-[110] overflow-hidden",
                            open ? "left-0" : "left-0"
                        )}
                    >
                        {/* Settings / Profile */}
                        <button
                            onClick={() => { setShowMenu(false); onNavigate('settings'); }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-foreground hover:bg-accent transition-colors"
                        >
                            <User className="h-4 w-4 flex-shrink-0" />
                            <span>Můj Profil</span>
                        </button>

                        <div className="border-t border-border" />

                        {/* Help */}
                        <button
                            onClick={() => { setShowMenu(false); onNavigate('help'); }}
                            className={cn(
                                "w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors",
                                activeSection === 'help'
                                    ? "text-primary bg-primary/5"
                                    : "text-foreground hover:bg-accent"
                            )}
                        >
                            <HelpCircle className="h-4 w-4 flex-shrink-0" />
                            <span>Nápověda</span>
                        </button>

                        <div className="border-t border-border" />

                        {/* Theme Toggle */}
                        <button
                            onClick={() => { toggleDarkMode(); }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-foreground hover:bg-accent transition-colors"
                        >
                            {isDarkMode ? (
                                <Sun className="h-4 w-4 flex-shrink-0" />
                            ) : (
                                <Moon className="h-4 w-4 flex-shrink-0" />
                            )}
                            <span>{isDarkMode ? 'Světlý režim' : 'Tmavý režim'}</span>
                        </button>

                        <div className="border-t border-border" />

                        {/* Logout */}
                        <button
                            onClick={() => { setShowMenu(false); logout(); }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-destructive hover:bg-destructive/10 transition-colors"
                        >
                            <LogOut className="h-4 w-4 flex-shrink-0" />
                            <span>Odhlásit se</span>
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const LogoImage = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { open, animate } = useSidebar();
    const { isDarkMode } = useDarkMode();
    return (
        <div className="flex items-center py-1 relative z-20 w-full overflow-hidden no-scrollbar justify-start pl-2 pr-10">
            <div className="flex-shrink-0" style={{ width: '44px', height: '44px' }}>
                <img
                    src={isDarkMode ? "/materials/ZKOUSKA-01.PNG" : "/materials/ZKOUSKA-DARK-01.png"}
                    alt="Dostaň se na práva"
                    className="object-contain w-full h-full"
                    style={{ transition: 'none', transform: 'none' }}
                />
            </div>
            <motion.span
                animate={{
                    opacity: open ? 1 : 0,
                    display: open ? "block" : "none"
                }}
                transition={{
                    duration: 0.2,
                    ease: "easeOut"
                }}
                className="font-black text-lg text-black dark:text-white whitespace-nowrap font-poppins ml-3"
                style={{
                    fontWeight: 900,
                    pointerEvents: open ? 'auto' : 'none'
                }}
            >
                Dostaň se na práva!
            </motion.span>
        </div>
    );
};

export const Logo = LogoImage;


const OspActivitiesMenu: React.FC<{ onAutoPrompt?: (prompt: string) => void }> = ({ onAutoPrompt }) => {
    const [activeMenu, setActiveMenu] = useState<'main' | 'verbal' | 'analytical'>('main');

    const handleTopicClick = (topicName: string, category: 'verbal' | 'analytical') => {
        const categoryName = category === 'verbal' ? 'verbálního oddílu' : 'analytického oddílu';
        onAutoPrompt?.(`Procvič se mnou ${topicName} z ${categoryName}.`);
        setActiveMenu('main');
    };

    if (activeMenu === 'verbal') {
        return (
            <div className="flex flex-col gap-2 px-2 pb-4 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="flex items-center gap-2 mb-2 px-1">
                    <button onClick={() => setActiveMenu('main')} className="p-1 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-md transition-colors text-neutral-500">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs font-bold text-neutral-500 uppercase tracking-wider">Verbální oddíl</span>
                </div>
                {VERBAL_TOPICS.map(topic => (
                    <button
                        key={topic.title}
                        onClick={() => handleTopicClick(topic.title, 'verbal')}
                        className="flex flex-col items-start gap-1 p-3 rounded-xl bg-card border border-border hover:border-brand-orange hover:shadow-sm transition-all text-left group"
                    >
                        <span className="text-sm font-semibold text-foreground group-hover:text-brand-orange transition-colors">{topic.title}</span>
                        <span className="text-[10px] text-muted-foreground leading-tight">{topic.desc}</span>
                    </button>
                ))}
            </div>
        );
    }

    if (activeMenu === 'analytical') {
        return (
            <div className="flex flex-col gap-2 px-2 pb-4 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="flex items-center gap-2 mb-2 px-1">
                    <button onClick={() => setActiveMenu('main')} className="p-1 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-md transition-colors text-neutral-500">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs font-bold text-neutral-500 uppercase tracking-wider">Analytický oddíl</span>
                </div>
                {ANALYTICAL_TOPICS.map(topic => (
                    <button
                        key={topic.title}
                        onClick={() => handleTopicClick(topic.title, 'analytical')}
                        className="flex flex-col items-start gap-1 p-3 rounded-xl bg-card border border-border hover:border-brand-orange hover:shadow-sm transition-all text-left group"
                    >
                        <span className="text-sm font-semibold text-foreground group-hover:text-brand-orange transition-colors">{topic.title}</span>
                        <span className="text-[10px] text-muted-foreground leading-tight">{topic.desc}</span>
                    </button>
                ))}
            </div>
        );
    }

    return (
        <div className="flex flex-col px-2 pb-4 animate-in fade-in slide-in-from-left-4 duration-300">
            <span className="text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-2 px-1 mt-0">Co chceš procvičovat?</span>
            <div className="grid grid-cols-2 gap-2">
                <button
                    onClick={() => setActiveMenu('verbal')}
                    className="flex flex-col items-center justify-center gap-2 p-3 rounded-xl bg-neutral-100 dark:bg-neutral-800 border-2 border-b-[4px] border-neutral-200 dark:border-neutral-700 hover:border-brand-orange hover:bg-orange-50 dark:hover:bg-orange-950/30 active:border-b-2 active:translate-y-[2px] transition-all group"
                >
                    <BookOpen className="w-6 h-6 text-neutral-500 group-hover:text-brand-orange transition-colors" />
                    <span className="text-[11px] font-bold text-center text-neutral-600 dark:text-neutral-300 group-hover:text-brand-orange transition-colors leading-tight">Verbální<br />oddíl</span>
                </button>
                <button
                    onClick={() => setActiveMenu('analytical')}
                    className="flex flex-col items-center justify-center gap-2 p-3 rounded-xl bg-neutral-100 dark:bg-neutral-800 border-2 border-b-[4px] border-neutral-200 dark:border-neutral-700 hover:border-brand-orange hover:bg-orange-50 dark:hover:bg-orange-950/30 active:border-b-2 active:translate-y-[2px] transition-all group"
                >
                    <Brain className="w-6 h-6 text-neutral-500 group-hover:text-brand-orange transition-colors" />
                    <span className="text-[11px] font-bold text-center text-neutral-600 dark:text-neutral-300 group-hover:text-brand-orange transition-colors leading-tight">Analytický<br />oddíl</span>
                </button>
                <button
                    onClick={() => onAutoPrompt?.(getDailyChallengePrompt())}
                    className="flex flex-col items-center justify-center gap-2 p-3 rounded-xl bg-neutral-100 dark:bg-neutral-800 border-2 border-b-[4px] border-neutral-200 dark:border-neutral-700 hover:border-brand-orange hover:bg-orange-50 dark:hover:bg-orange-950/30 active:border-b-2 active:translate-y-[2px] transition-all group"
                >
                    <Target className="w-6 h-6 text-neutral-500 group-hover:text-brand-orange transition-colors" />
                    <span className="text-[11px] font-bold text-center text-neutral-600 dark:text-neutral-300 group-hover:text-brand-orange transition-colors leading-tight">Denní<br />výzva</span>
                </button>
                <button
                    onClick={() => onAutoPrompt?.(QUICK_DRILL_PROMPT)}
                    className="flex flex-col items-center justify-center gap-2 p-3 rounded-xl bg-neutral-100 dark:bg-neutral-800 border-2 border-b-[4px] border-neutral-200 dark:border-neutral-700 hover:border-brand-orange hover:bg-orange-50 dark:hover:bg-orange-950/30 active:border-b-2 active:translate-y-[2px] transition-all group"
                >
                    <Zap className="w-6 h-6 text-neutral-500 group-hover:text-brand-orange transition-colors" />
                    <span className="text-[11px] font-bold text-center text-neutral-600 dark:text-neutral-300 group-hover:text-brand-orange transition-colors leading-tight">Rychlý<br />drill</span>
                </button>
            </div>
        </div>
    );
};
